/**
 * useMapDrillDown Hook
 *
 * Manages drill-down state for the climb map view, enabling smooth transitions
 * between region overview and pin detail views based on zoom level.
 *
 * Features:
 * - Automatic view mode switching based on scale threshold
 * - Animated zoom to region polygons
 * - Animated zoom to specific points (e.g., route pins)
 * - Current region detection based on viewport center
 * - Animation state tracking
 *
 * @example
 * ```tsx
 * const mapStageRef = useRef<MapStageRef>(null);
 *
 * const drillDown = useMapDrillDown({
 *   regions: floorPlan.regions,
 *   initialScale,
 *   imageWidth,
 *   imageHeight,
 *   containerWidth,
 *   containerHeight,
 *   mapStageRef,
 * });
 *
 * // Drill down into a region
 * drillDown.zoomToRegion('Main Wall');
 *
 * // Center on a route pin
 * drillDown.zoomToPoint({ x: 0.5, y: 0.3 }, { offsetY: 0.15 });
 * ```
 */

import { useState, useCallback, useMemo, useEffect, type RefObject } from 'react';
import type { Point, WallSectionRegion } from '@flash/shared';
import type { MapStageRef, ViewportState } from '@/components/gym-map/MapStage';
import { detectWallSection } from '@/utils/geometry';

/**
 * View mode for the map
 */
export type MapViewMode = 'regions' | 'pins';

/**
 * Options for useMapDrillDown hook
 */
export interface UseMapDrillDownOptions {
  /** Wall section regions from floor plan */
  regions: WallSectionRegion[];
  /** Initial scale when map is first loaded (fit-to-container) */
  initialScale: number;
  /** Floor plan image width in pixels */
  imageWidth: number;
  /** Floor plan image height in pixels */
  imageHeight: number;
  /** Container width in pixels */
  containerWidth: number;
  /** Container height in pixels */
  containerHeight: number;
  /** Ref to MapStage for imperative control */
  mapStageRef: RefObject<MapStageRef | null>;
  /** Scale threshold multiplier for switching modes (default: 1.8) */
  scaleThreshold?: number;
  /** Target scale when zooming into a region (multiplier of initialScale, default: 2.5) */
  regionZoomMultiplier?: number;
}

/**
 * Return value from useMapDrillDown hook
 */
export interface UseMapDrillDownResult {
  /** Current view mode based on scale */
  viewMode: MapViewMode;
  /** Current region at viewport center, or null */
  currentRegion: string | null;
  /** Current viewport state */
  viewport: ViewportState;
  /** Whether an animation is in progress */
  isAnimating: boolean;
  /** Handle viewport changes from MapStage */
  handleViewportChange: (viewport: ViewportState) => void;
  /** Zoom into a region by name */
  zoomToRegion: (regionName: string) => Promise<void>;
  /** Zoom to a specific point on the map */
  zoomToPoint: (
    point: Point,
    options?: { offsetY?: number; targetScale?: number }
  ) => Promise<void>;
}

/** Default scale threshold multiplier (viewMode switches at this * initialScale) */
const DEFAULT_SCALE_THRESHOLD = 1.8;

/** Default zoom multiplier when drilling into a region */
const DEFAULT_REGION_ZOOM_MULTIPLIER = 2.5;

/** Animation duration for zoom transitions */
const ZOOM_ANIMATION_DURATION = 400;

/**
 * Calculate the bounding box of a polygon in normalized coordinates
 */
const calculatePolygonBounds = (
  polygon: Point[]
): { minX: number; maxX: number; minY: number; maxY: number } => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const point of polygon) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }

  return { minX, maxX, minY, maxY };
};

/**
 * Calculate the center point of a polygon
 */
const calculatePolygonCenter = (polygon: Point[]): Point => {
  const bounds = calculatePolygonBounds(polygon);
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
};

/**
 * Calculate distance between two points (normalized coordinates)
 */
const calculateDistance = (p1: Point, p2: Point): number => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Maximum distance (in normalized coords) from a region center before
 * the sticky region clears. ~0.24 = about 24% of the map width/height.
 */
const MAX_STICKY_DISTANCE = 0.24;

/**
 * useMapDrillDown Hook
 *
 * Manages the drill-down state for transitioning between region and pin views.
 */
export const useMapDrillDown = ({
  regions,
  initialScale,
  imageWidth,
  imageHeight,
  containerWidth,
  containerHeight,
  mapStageRef,
  scaleThreshold = DEFAULT_SCALE_THRESHOLD,
  regionZoomMultiplier = DEFAULT_REGION_ZOOM_MULTIPLIER,
}: UseMapDrillDownOptions): UseMapDrillDownResult => {
  // Viewport state
  const [viewport, setViewport] = useState<ViewportState>({
    x: 0,
    y: 0,
    scale: initialScale,
  });

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);

  // Derive view mode from scale
  const viewMode = useMemo<MapViewMode>(() => {
    const threshold = initialScale * scaleThreshold;
    return viewport.scale >= threshold ? 'pins' : 'regions';
  }, [viewport.scale, initialScale, scaleThreshold]);

  // Calculate the center point of the viewport in normalized image coordinates
  const viewportCenter = useMemo<Point>(() => {
    // Viewport center in screen space
    const screenCenterX = containerWidth / 2;
    const screenCenterY = containerHeight / 2;

    // Convert to image coordinates (accounting for pan and scale)
    // screen_pos = image_pos * scale + offset
    // image_pos = (screen_pos - offset) / scale
    const imageCenterX = (screenCenterX - viewport.x) / viewport.scale;
    const imageCenterY = (screenCenterY - viewport.y) / viewport.scale;

    // Normalize to 0-1 range
    return {
      x: imageCenterX / imageWidth,
      y: imageCenterY / imageHeight,
    };
  }, [viewport, containerWidth, containerHeight, imageWidth, imageHeight]);

  // Detect which region the viewport center is currently in
  const detectedRegion = useMemo<string | null>(() => {
    if (regions.length === 0) return null;
    return detectWallSection(viewportCenter, regions);
  }, [viewportCenter, regions]);

  // Sticky region state: persists when panning through empty space
  // Only updates when entering a new region or zooming out to regions mode
  const [stickyRegion, setStickyRegion] = useState<string | null>(null);

  // Update sticky region based on detection, view mode, and distance
  useEffect(() => {
    // Skip updates during animation to preserve sticky region set by zoomToRegion
    if (isAnimating) return;

    if (viewMode === 'regions') {
      // Clear sticky region when zoomed out to regions overview
      setStickyRegion(null);
    } else if (detectedRegion !== null) {
      // Update sticky region when we enter a new region
      setStickyRegion(detectedRegion);
    } else if (stickyRegion !== null) {
      // Check if we've moved too far from the sticky region
      const stickyRegionData = regions.find((r) => r.wallSectionName === stickyRegion);
      if (stickyRegionData) {
        const regionCenter = calculatePolygonCenter(stickyRegionData.polygon);
        const distance = calculateDistance(viewportCenter, regionCenter);
        if (distance > MAX_STICKY_DISTANCE) {
          // Too far from the sticky region, clear it
          setStickyRegion(null);
        }
      }
    }
  }, [detectedRegion, viewMode, viewportCenter, stickyRegion, regions, isAnimating]);

  // Return the sticky region (persists through empty space when in pins mode)
  // During animation, show the sticky region even if viewMode hasn't transitioned yet
  const currentRegion = viewMode === 'regions' && !isAnimating ? null : stickyRegion;

  // Handle viewport changes from MapStage
  const handleViewportChange = useCallback((newViewport: ViewportState) => {
    setViewport(newViewport);
  }, []);

  // Zoom into a region by name
  const zoomToRegion = useCallback(
    async (regionName: string): Promise<void> => {
      const region = regions.find((r) => r.wallSectionName === regionName);
      if (!region || !mapStageRef.current) {
        return;
      }

      // Force-set sticky region immediately on click.
      // This ensures oddly-shaped regions (where viewport center may fall
      // outside the polygon after zoom) still show their name.
      setStickyRegion(regionName);

      const bounds = calculatePolygonBounds(region.polygon);

      // Calculate region center in normalized coords
      const centerX = (bounds.minX + bounds.maxX) / 2;
      const centerY = (bounds.minY + bounds.maxY) / 2;

      // Calculate target scale based on region size
      // We want the region to be visible but also ensure we cross the threshold
      const regionWidth = (bounds.maxX - bounds.minX) * imageWidth;
      const regionHeight = (bounds.maxY - bounds.minY) * imageHeight;

      const scaleToFitWidth = (containerWidth * 0.7) / regionWidth;
      const scaleToFitHeight = (containerHeight * 0.7) / regionHeight;

      // For elongated regions, the min of fit scales can be too small
      // Use the LARGER of the two fit scales to ensure we zoom in enough
      // but cap it at the max zoom multiplier
      const scaleToShowRegion = Math.max(scaleToFitWidth, scaleToFitHeight);

      // Ensure we always cross the threshold to show pins (add 20% margin)
      const minScaleForPins = initialScale * scaleThreshold * 1.2;

      // Target scale: at least enough to show pins, ideally showing the region well
      // but not more than our max zoom
      const maxScale = initialScale * regionZoomMultiplier;
      const targetScale = Math.min(Math.max(scaleToShowRegion, minScaleForPins), maxScale);

      // Calculate target position to center the region
      const targetX = containerWidth / 2 - centerX * imageWidth * targetScale;
      const targetY = containerHeight / 2 - centerY * imageHeight * targetScale;

      setIsAnimating(true);
      try {
        await mapStageRef.current.animateToViewport(
          { x: targetX, y: targetY, scale: targetScale },
          ZOOM_ANIMATION_DURATION
        );
      } finally {
        setIsAnimating(false);
      }
    },
    [
      regions,
      mapStageRef,
      imageWidth,
      imageHeight,
      containerWidth,
      containerHeight,
      initialScale,
      regionZoomMultiplier,
      scaleThreshold,
    ]
  );

  // Zoom to a specific point on the map
  const zoomToPoint = useCallback(
    async (point: Point, options?: { offsetY?: number; targetScale?: number }): Promise<void> => {
      if (!mapStageRef.current) return;

      const { offsetY = 0, targetScale } = options ?? {};

      // Use current scale or specified target, ensuring we're in pins mode
      const threshold = initialScale * scaleThreshold;
      const currentScale = mapStageRef.current.getViewport().scale;
      const newScale = targetScale ?? Math.max(currentScale, threshold * 1.2);

      // Convert normalized point to pixel coordinates
      const pixelX = point.x * imageWidth;
      const pixelY = point.y * imageHeight;

      // Calculate target position to center the point
      // Offset moves the point UP from center (negative Y direction) for route card visibility
      const offsetPixels = containerHeight * offsetY;
      const targetX = containerWidth / 2 - pixelX * newScale;
      const targetY = containerHeight / 2 - pixelY * newScale - offsetPixels;

      setIsAnimating(true);
      try {
        await mapStageRef.current.animateToViewport(
          { x: targetX, y: targetY, scale: newScale },
          ZOOM_ANIMATION_DURATION
        );
      } finally {
        setIsAnimating(false);
      }
    },
    [
      mapStageRef,
      imageWidth,
      imageHeight,
      containerWidth,
      containerHeight,
      initialScale,
      scaleThreshold,
    ]
  );

  return {
    viewMode,
    currentRegion,
    viewport,
    isAnimating,
    handleViewportChange,
    zoomToRegion,
    zoomToPoint,
  };
};

export default useMapDrillDown;
